import React, { useEffect, useState } from 'react';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { PlusCircle, Edit2, Trash2, AlertCircle, Clock, Users } from 'lucide-react';
import { FacilityModal } from './FacilityModal';
import { useUserRole } from '../../hooks/useUserRole';
import { ConfirmationModal } from '../common/ConfirmationModal';

interface Facility {
  id: string;
  name: string;
  type: string;
  capacity: number;
  description: string;
  status: 'available' | 'maintenance' | 'occupied';
  openingHours: OpeningHours[];
  blockedTimes: BlockedTime[];
}

interface OpeningHours {
  dayOfWeek: number;
  start: string;
  end: string;
  closed?: boolean;
}

interface BlockedTime {
  id: string;
  start: Date;
  end: Date;
  reason: string;
  recurring?: boolean;
  recurrenceEnd?: Date;
  allDay?: boolean;
}

const DAYS_OF_WEEK = [
  'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 
  'Freitag', 'Samstag', 'Sonntag'
];

const DEFAULT_OPENING_HOURS: OpeningHours[] = DAYS_OF_WEEK.map((_, index) => ({
  dayOfWeek: index,
  start: '08:00',
  end: '20:00',
  closed: false
}));

export const FacilitiesList = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [facilityToDelete, setFacilityToDelete] = useState<Facility | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isManager, isApproved } = useUserRole();

  useEffect(() => {
    const q = query(collection(db, 'facilities'));
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const facilitiesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          openingHours: doc.data().openingHours || DEFAULT_OPENING_HOURS,
          blockedTimes: doc.data().blockedTimes?.map((time: any) => ({
            ...time,
            start: time.start.toDate(),
            end: time.end.toDate(),
            recurrenceEnd: time.recurrenceEnd?.toDate()
          })) || []
        })) as Facility[];
        setFacilities(facilitiesData);
        setError(null);
      },
      (err) => {
        console.error('Error loading facilities:', err);
        setError('Anlagen konnten nicht geladen werden');
      }
    );

    return () => unsubscribe();
  }, []);

  const handleAddEdit = async (facilityData: Omit<Facility, 'id'>) => {
    if (!isManager || !isApproved) {
      setError('Nur zugelassene Verwalter können Anlagen verwalten.');
      return;
    }

    try {
      const processedData = {
        ...facilityData,
        blockedTimes: facilityData.blockedTimes.map(time => ({
          ...time,
          start: Timestamp.fromDate(time.start),
          end: Timestamp.fromDate(time.end),
          recurrenceEnd: time.recurrenceEnd ? Timestamp.fromDate(time.recurrenceEnd) : null
        }))
      };

      if (editingFacility) {
        await updateDoc(doc(db, 'facilities', editingFacility.id), processedData);
      } else {
        await addDoc(collection(db, 'facilities'), processedData);
      }
      setIsModalOpen(false);
      setEditingFacility(null);
      setError(null);
    } catch (error: any) {
      console.error('Error saving facility:', error);
      setError('Beim Speichern der Anlage ist ein Fehler aufgetreten.');
    }
  };

  const handleDelete = async () => {
    if (!facilityToDelete) return;
    if (!isManager || !isApproved) {
      setError('Nur zugelassene Verwalter können Anlagen löschen.');
      return;
    }

    try {
      await deleteDoc(doc(db, 'facilities', facilityToDelete.id));
      setFacilityToDelete(null);
      setError(null);
    } catch (error: any) {
      console.error('Error deleting facility:', error);
      setError('Beim Löschen der Anlage ist ein Fehler aufgetreten.');
    }
  };

  const getStatusColor = (status: Facility['status']) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Anlagen</h2>
        {isManager && isApproved && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 sm:py-2 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Anlage hinzufügen
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-300 flex-shrink-0" />
            <p className="ml-3 text-sm text-red-700 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {facilities.map((facility) => (
          <div
            key={facility.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            <div className="p-4 sm:p-6">
              <div className="flex flex-col space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                      {facility.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {facility.description}
                    </p>
                  </div>

                  {isManager && isApproved && (
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => {
                          setEditingFacility(facility);
                          setIsModalOpen(true);
                        }}
                        className="p-2 rounded-lg text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:text-indigo-300 dark:hover:bg-indigo-900/50"
                        title="Anlage bearbeiten"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setFacilityToDelete(facility)}
                        className="p-2 rounded-lg text-red-600 hover:text-red-900 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/50"
                        title="Anlage löschen"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(facility.status)}`}>
                    {facility.status === 'available' ? 'Verfügbar' :
                     facility.status === 'maintenance' ? 'Wartung' :
                     'Belegt'}
                  </span>

                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Users className="h-4 w-4 mr-1" />
                    <span>{facility.capacity}</span>
                  </div>

                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>
                      {facility.openingHours[0].closed ? 'Geschlossen' :
                       `${facility.openingHours[0].start} - ${facility.openingHours[0].end}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isManager && isApproved && (
        <>
          <FacilityModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditingFacility(null);
            }}
            onSave={handleAddEdit}
            facility={editingFacility}
          />

          <ConfirmationModal
            isOpen={!!facilityToDelete}
            onClose={() => setFacilityToDelete(null)}
            onConfirm={handleDelete}
            title="Anlage löschen"
            message={`Möchten Sie die Anlage "${facilityToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
            confirmLabel="Löschen"
            confirmStyle="danger"
          />
        </>
      )}
    </div>
  );
};